/**
 * useModalManager - Pure modal state management
 *
 * This hook provides pure state management for modals with no business logic.
 * Business logic should be handled in the components or other hooks that use this.
 */

import { useState, useCallback, useMemo } from 'react';

/**
 * Modal types supported by the meal plan editor
 */
export const MODAL_TYPES = {
    ADD_SWAP_RECIPE: 'addSwapRecipe',
    MEAL_DETAILS: 'mealDetails',
    REPEAT_MEAL: 'repeatMeal',
    EDIT_MEAL: 'editMeal',
    EDIT_MEAL_SERVINGS: 'editMealServings',
    EDIT_RECIPE: 'editRecipe',
    EDIT_FOOD: 'editFood',
    NUTRITION: 'showNutrition',
    GROCERIES: 'showGroceries',
    RECOMMENDATIONS: 'showRecommendations',
    RECOMMEND: 'recommend',
    PICK_DATE: 'pickDate',
    OVERLAP_RESOLUTION: 'overlapResolution',
    PROFILE: 'showProfile',
    BAA: 'baa',
    LOG_PORTIONS: 'logPortions',
    LEFTOVER_OFFSETS: 'leftoverOffsets'
};

/**
 * Hook for managing modal state - pure state management, no business logic
 *
 * @param {Object} config - Configuration object
 * @returns {Object} Modal state management interface
 */
export const useModalManager = (config = {}) => {
    const { permissions = {} } = config;
    // Core modal state - tracks which modals are open
    const [openModals, setOpenModals] = useState({});

    // Context state for modals that need additional data
    const [addSwapContext, setAddSwapContext] = useState(null);
    const [mealsToRepeat, setMealsToRepeat] = useState(null);
    const [selectedMealForDetails, setSelectedMealForDetails] = useState(null);
    const [datePickerContext, setDatePickerContext] = useState(null);
    const [overlapResolutionContext, setOverlapResolutionContext] = useState(null);
    const [editMealServingsContext, setEditMealServingsContext] = useState(null);
    const [editRecipeContext, setEditRecipeContext] = useState(null);
    const [leftoverOffsetsContext, setLeftoverOffsetsContext] = useState(null);
    const [editFoodContext, setEditFoodContext] = useState(null);
    const [recommendContext, setRecommendContext] = useState(null);
    const [baaContext, setBaaContext] = useState(null);
    const [logPortionsContext, setLogPortionsContext] = useState(null);
    const [pickDateContext, setPickDateContext] = useState(null);

    /**
     * Generic modal open/close functions
     */
    const openModal = useCallback((modalKey, data = true) => {
        setOpenModals(prev => ({ ...prev, [modalKey]: data }));
    }, []);

    const closeModal = useCallback((modalKey) => {
        setOpenModals(prev => {
            const { [modalKey]: removed, ...rest } = prev;
            return rest;
        });
    }, []);

    const closeAllModals = useCallback(() => {
        setOpenModals({});
        // Also clear all context data
        setAddSwapContext(null);
        setMealsToRepeat(null);
        setSelectedMealForDetails(null);
        setDatePickerContext(null);
        setOverlapResolutionContext(null);
        setEditMealServingsContext(null);
        setRecommendContext(null);
        setBaaContext(null);
        setLogPortionsContext(null);
        setPickDateContext(null);
    }, []);

    const isModalOpen = useCallback((modalKey) => {
        return !!openModals[modalKey];
    }, [openModals]);

    const startRepeatMeal = useCallback((context) => {
        setMealsToRepeat(context);
        openModal(MODAL_TYPES.REPEAT_MEAL);
    }, [openModal]);

    /**
     * Convenience functions for common modal operations
     */
    const openOverlapModal = useCallback((overlapData) => {
        setOverlapResolutionContext(overlapData);
        openModal(MODAL_TYPES.OVERLAP_RESOLUTION);
    }, [openModal]);

    const closeOverlapModal = useCallback(() => {
        setOverlapResolutionContext(null);
        closeModal(MODAL_TYPES.OVERLAP_RESOLUTION);
    }, [closeModal]);

    const openMealDetailsModal = useCallback((context) => {
        setSelectedMealForDetails(context);
        openModal(MODAL_TYPES.MEAL_DETAILS);
    }, [openModal]);

    const closeMealDetailsModal = useCallback(() => {
        setSelectedMealForDetails(null);
        closeModal(MODAL_TYPES.MEAL_DETAILS);
    }, [closeModal]);

    const openAddSwapRecipeModal = useCallback((context) => {
        setAddSwapContext(context);
        openModal(MODAL_TYPES.ADD_SWAP_RECIPE);
    }, [openModal]);

    const showGroceries = useCallback(() => {
        openModal(MODAL_TYPES.GROCERIES);
    }, [openModal]);

    const closeGroceries = useCallback(() => {
        closeModal(MODAL_TYPES.GROCERIES);
    }, [closeModal]);

    const showNutrition = useCallback(() => {
        openModal(MODAL_TYPES.NUTRITION);
    }, [openModal]);

    const showProfile = useCallback(() => {
        openModal(MODAL_TYPES.PROFILE);
    }, [openModal]);

    const closeProfile = useCallback(() => {
        closeModal(MODAL_TYPES.PROFILE);
    }, [closeModal]);

    const editMealBatches = useCallback((meal) => {
        if (permissions.inhibitSwap) return;
        setEditMealServingsContext({ meal });
        openModal(MODAL_TYPES.EDIT_MEAL_SERVINGS);
    }, [openModal, permissions.inhibitSwap]);

    const openLeftoverOffsetsModal = useCallback((leftoverParams) => {
        setLeftoverOffsetsContext(leftoverParams);
        openModal(MODAL_TYPES.LEFTOVER_OFFSETS);
    }, [openModal]);

    const closeLeftoverOffsetsModal = useCallback(() => {
        setLeftoverOffsetsContext(null);
        closeModal(MODAL_TYPES.LEFTOVER_OFFSETS);
    }, [closeModal]);

    const startRecommend = useCallback((item, type = 'recipe') => {
        setRecommendContext({ item, type });
        openModal(MODAL_TYPES.RECOMMEND);
    }, [openModal]);

    const closeRecommend = useCallback(() => {
        setRecommendContext(null);
        closeModal(MODAL_TYPES.RECOMMEND);
    }, [closeModal]);

    const editRecipe = useCallback((recipe, meal) => {
        setEditRecipeContext({ recipe, meal });
        openModal(MODAL_TYPES.EDIT_RECIPE);
    }, [openModal]);

    const closeEditRecipe = useCallback(() => {
        setEditRecipeContext(null);
        closeModal(MODAL_TYPES.EDIT_RECIPE);
    }, [closeModal]);

    const editFood = useCallback((food, meal) => {
        setEditFoodContext({ food, meal });
        openModal(MODAL_TYPES.EDIT_FOOD);
    }, [openModal]);

    const closeEditFood = useCallback(() => {
        setEditFoodContext(null);
        closeModal(MODAL_TYPES.EDIT_FOOD);
    }, [closeModal]);

    const showBAA = useCallback((onAccept) => {
        setBaaContext({ onAccept });
        openModal(MODAL_TYPES.BAA);
    }, [openModal]);

    const closeBAA = useCallback(() => {
        setBaaContext(null);
        closeModal(MODAL_TYPES.BAA);
    }, [closeModal]);

    const showLogPortions = useCallback((contents, config = {}) => {
        setLogPortionsContext({
            contents,
            ...config
        });
        openModal(MODAL_TYPES.LOG_PORTIONS);
    }, [openModal]);

    const closeLogPortions = useCallback(() => {
        setLogPortionsContext(null);
        closeModal(MODAL_TYPES.LOG_PORTIONS);
    }, [closeModal]);

    const showPickDate = useCallback((config = {}) => {
        setPickDateContext(config);
        openModal(MODAL_TYPES.PICK_DATE);
    }, [openModal]);

    const closePickDate = useCallback(() => {
        setPickDateContext(null);
        closeModal(MODAL_TYPES.PICK_DATE);
    }, [closeModal]);

    // Get modal-specific data
    const getModalData = useCallback((modalKey) => {
        switch (modalKey) {
            case MODAL_TYPES.ADD_SWAP_RECIPE:
                return addSwapContext;
            case MODAL_TYPES.REPEAT_MEAL:
                return mealsToRepeat;
            case MODAL_TYPES.MEAL_DETAILS:
                return selectedMealForDetails;
            case MODAL_TYPES.EDIT_MEAL:
                return openModals[MODAL_TYPES.EDIT_MEAL];
            case MODAL_TYPES.EDIT_MEAL_SERVINGS:
                return editMealServingsContext;
            case MODAL_TYPES.EDIT_RECIPE:
                return editRecipeContext;
            case MODAL_TYPES.EDIT_FOOD:
                return editFoodContext;
            case MODAL_TYPES.OVERLAP_RESOLUTION:
                return overlapResolutionContext;
            case MODAL_TYPES.PICK_DATE:
                return pickDateContext;
            case MODAL_TYPES.BAA:
                return baaContext;
            case MODAL_TYPES.LOG_PORTIONS:
                return logPortionsContext;
            default:
                return openModals[modalKey];
        }
    }, [openModals, addSwapContext, mealsToRepeat, selectedMealForDetails, editMealServingsContext, editRecipeContext, editFoodContext, overlapResolutionContext, datePickerContext, baaContext, logPortionsContext, pickDateContext]);

    // Build the return object
    return useMemo(() => ({
        // Core modal state
        openModals,

        // Generic modal control
        openModal,
        closeModal,
        closeAllModals,
        isModalOpen,
        startRepeatMeal,

        // Convenience functions
        openOverlapModal,
        closeOverlapModal,
        openMealDetailsModal,
        closeMealDetailsModal,
        openAddSwapRecipeModal,
        editMealBatches,
        openLeftoverOffsetsModal,
        closeLeftoverOffsetsModal,
        editRecipe,
        closeEditRecipe,
        editFood,
        closeEditFood,
        showGroceries,
        closeGroceries,
        showNutrition,
        showProfile,
        closeProfile,
        startRecommend,
        closeRecommend,
        showBAA,
        closeBAA,
        showLogPortions,
        closeLogPortions,
        showPickDate,
        closePickDate,

        // Modal data access
        getModalData,

        // Context state (for components that need direct access)
        addSwapContext,
        mealsToRepeat,
        selectedMealForDetails,
        editMealServingsContext,
        leftoverOffsetsContext,
        editRecipeContext,
        editFoodContext,
        recommendContext,
        overlapResolutionContext,
        datePickerContext,
        baaContext,
        logPortionsContext,
        pickDateContext,

        // Context setters (for direct manipulation when needed)
        setMealDetailsContext: setSelectedMealForDetails,
        setAddSwapModalContext: setAddSwapContext,
        setEditMealServingsContext,

        // Computed state
        hasOpenModals: Object.keys(openModals).length > 0,

        // Specific modal state queries
        isOverlapModalOpen: isModalOpen(MODAL_TYPES.OVERLAP_RESOLUTION),
        isMealDetailsModalOpen: isModalOpen(MODAL_TYPES.MEAL_DETAILS),
        isAddSwapRecipeModalOpen: isModalOpen(MODAL_TYPES.ADD_SWAP_RECIPE)
    }), [
        openModals,
        openModal,
        closeModal,
        closeAllModals,
        startRepeatMeal,
        isModalOpen,
        openOverlapModal,
        closeOverlapModal,
        openMealDetailsModal,
        closeMealDetailsModal,
        openAddSwapRecipeModal,
        editMealBatches,
        editRecipe,
        closeEditRecipe,
        editFood,
        closeEditFood,
        showGroceries,
        closeGroceries,
        showNutrition,
        showProfile,
        closeProfile,
        startRecommend,
        closeRecommend,
        showBAA,
        closeBAA,
        showLogPortions,
        closeLogPortions,
        showPickDate,
        closePickDate,
        getModalData,
        addSwapContext,
        mealsToRepeat,
        selectedMealForDetails,
        editMealServingsContext,
        editRecipeContext,
        editFoodContext,
        recommendContext,
        overlapResolutionContext,
        datePickerContext,
        baaContext,
        logPortionsContext,
        setSelectedMealForDetails,
        setAddSwapContext,
        setEditMealServingsContext
    ]);
};

export default useModalManager;
